/**
 * The Waslo AI entry in Chatwoot's primary sidebar.
 *
 * `Sidebar.vue` gets one import and one line; everything that can change lives here,
 * where upstream cannot reach it. THE PLAN, §4 P2 and §6 R-1.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE GROUP HEADER IS A CONTAINER, NOT A DESTINATION.
 *
 * It used to be both, and carried `to: children[0].to` to make that safe. The reason is
 * worth keeping, because it explains why the `to` is gone rather than merely absent:
 *
 *   1. `SidebarGroupHeader.vue:25-40` renders `<component :is="to ? 'router-link' : 'div'"
 *      :to="to" … @click.stop="emit('toggle')">` — the router-link navigates to `to`
 *      AND the same click emits `toggle`.
 *   2. `SidebarGroup.vue:207-218` `toggleTrigger` handles that emit and, when the group
 *      is not expanded and has no active child, independently
 *      `router.push(accessibleItems[0].to)`.
 *
 * Both ran on the same click and nothing ordered them, so equality was the only shape
 * that made the landing deterministic. It also had a defect equality could not fix:
 * clicking the header while already on a Waslo page threw the owner back to Overview.
 *
 * Dropping `to` removes the race outright rather than defusing it. `toggleTrigger` still
 * pushes `accessibleItems[0].to` — Overview — so a click on a collapsed group still
 * lands where it always did, and the header on an expanded group now only toggles.
 * This is also the shape every one of upstream's container groups uses: of the nine,
 * only `Inbox` carries a `to`, and it has no children.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ORDER IS FREQUENCY × STAKES, NOT BUILD ORDER. The previous order was the order the
 * features shipped in (P4, P5, P3, P7, P6, P9, P8), which put the screens an owner
 * touches least above the ones she checks weekly. Overview answers "is it working"; the
 * three under it are the loop she actually runs; Tasks is occasional.
 *
 * EVERY DESTINATION CARRIES AN ICON. `SidebarGroup.vue:324` `v-bind="child"` passes the
 * whole child object down and `SidebarGroupLeaf.vue:58` renders `<span v-if="icon">`, so
 * this costs one key per row and no component change. Twelve text-only rows were the
 * reason the group could not be scanned.
 *
 * WHAT IS NOT HERE ANY MORE, and where it went — four rows left in rebuild 2:
 *
 *   • `Settings` — the caption is gone because its children are. Two were merged into
 *     pages above and the third moved into the developer sub-group, so there was nothing
 *     left for it to caption.
 *   • `Channels` → a section of Overview. `Permissions` → the "What it may do" section of
 *     Personality. Neither was a screen an owner visited twice; both are answers to a
 *     question the page above them was already asking.
 *   • `Test it` → a button in the header of Overview, Answers and Personality. It is an
 *     ACTION, not a place, and as a row it had no inbound link from any of the three
 *     screens that create the reason to press it.
 *
 * All four ROUTES still resolve — see `specs/nav-registration.spec.js`. Deleting a name
 * the sidebar might still resolve does not 404, it blanks the primary nav.
 *
 * AND TWO MORE LEFT IN THE NAVIGATION CONSOLIDATION — `Menus` and `Forms`, eleven rows down
 * to nine. Neither was merged away; each became a TAB on the row it was already sitting
 * beside, because both old entries explained themselves by pointing at their neighbour:
 * Menus said an option "can START one: the two are built together", Forms said it and
 * Approved messages are "both about something Meta holds ... and both scoped to ONE number".
 * A row whose reason for existing is the row above it is a tab.
 *
 * THE TAB IS A QUERY PARAMETER (`?tab=menus`, `?tab=forms`), never a route, so the route
 * table grows by zero records and `assert_waslo_seam.sh`'s counts do not move. Both retired
 * names stay registered as redirects, and their `activeOn` entries moved onto the two
 * surviving rows — see each one below. There is NO third nav builder: `wasloSidebarItems`
 * and `wasloCampaignsSidebarItems` are still the only two, which is what keeps that script's
 * `PROV_ARGS ≥ 2` and `wasloLocale ≥ 3` exactly where they are.
 *
 * There is no `featureFlag` and no `installationTypes` anywhere in this section — see the
 * header of `wasloai.routes.js` for why that is the difference between this entry and
 * Captain's, which is defined in the same array and renders for nobody here.
 */

import { wasloT } from './i18n.js';

export const WASLO_SIDEBAR_ICON = 'i-lucide-bot-message-square';

/**
 * THE LOCALE IS AN ARGUMENT, and it is not decoration. Every string on this entry comes
 * from `W/i18n.js`, whose tables upstream's `t` has never heard of — so without a locale
 * `wasloT` can only answer in English, and an Arabic account renders Arabic PAGES under
 * an English NAV. `Sidebar.vue` reads it from the same composer its `t` comes from, so
 * the row and the page it opens can never disagree about the language. The rationale for
 * passing it rather than looking it up is in the header of `i18n.js`.
 *
 * @param {(key: string) => string} t  Chatwoot's global translator, from `Sidebar.vue`.
 * @param {(name: string, params?: object, query?: object) => object} accountScopedRoute
 * @param {string} [locale]  The locale the rest of the screen is using. Absent → English.
 * @returns {object} A `menuItems` entry for `components-next/sidebar/Sidebar.vue`.
 */
export function wasloSidebarItem(t, accountScopedRoute, locale) {
  const children = [
    {
      name: 'WasloOverview',
      label: wasloT(t, 'WASLO.NAV.OVERVIEW', locale),
      icon: 'i-lucide-gauge',
      to: accountScopedRoute('waslo_overview'),
      activeOn: [
        'waslo_overview',
        'waslo_setup',
        'waslo_connect_ai',
        'waslo_credits_buy',
        'waslo_credits_return',
      ],
    },
    {
      name: 'WasloAnswers',
      label: wasloT(t, 'WASLO.NAV.ANSWERS', locale),
      icon: 'i-lucide-book-open',
      to: accountScopedRoute('waslo_answers'),
      activeOn: ['waslo_answers', 'waslo_answer_source'],
    },
    {
      // Promoted above Personality and Tasks: "how is it doing" is a weekly question and
      // the two below it are set-once-and-revisit.
      name: 'WasloActivity',
      label: wasloT(t, 'WASLO.NAV.ACTIVITY', locale),
      icon: 'i-lucide-scroll-text',
      to: accountScopedRoute('waslo_activity', {}, { range: 30 }),
      activeOn: ['waslo_activity', 'waslo_activity_run'],
    },
    {
      name: 'WasloPersonality',
      label: wasloT(t, 'WASLO.NAV.PERSONALITY', locale),
      icon: 'i-lucide-notebook-pen',
      to: accountScopedRoute('waslo_personality'),
      activeOn: ['waslo_personality', 'waslo_personality_first_draft'],
    },
    {
      // MENUS IS A TAB ON THIS ROW NOW, not a row of its own — and the reason was already
      // written one entry below, where the Menus row used to explain itself: "an option can
      // START one: the two are built together." Two rows for one job is what that sentence
      // was apologising for. `TasksPage` carries `[ Tasks | Menus ]`, the tab rides in the
      // QUERY (`?tab=menus`) so the route table does not grow, and `waslo_menus` stays
      // registered as a redirect onto it (`wasloai.routes.js`).
      //
      // THE THREE MENU ROUTE NAMES MOVE ONTO THIS `activeOn` RATHER THAN BEING DROPPED.
      // `SidebarGroup` lights a row by matching the current route name against this list,
      // so without them an owner standing in the menu editor — a screen this row is now the
      // only way to reach — would see NO row highlighted in the whole section, which reads
      // as "you have left Waslo AI". The redirect target (`waslo_tasks`) covers the index;
      // the two editors have records of their own and have to be claimed by name.
      name: 'WasloTasks',
      label: wasloT(t, 'WASLO.NAV.TASKS', locale),
      icon: 'i-lucide-list-checks',
      to: accountScopedRoute('waslo_tasks'),
      activeOn: [
        'waslo_tasks',
        'waslo_task_new',
        'waslo_task_edit',
        // P-4.10: Arrange form is part of the task it edits.
        'waslo_task_form',
        'waslo_menus',
        'waslo_menu_new',
        'waslo_menu_edit',
      ],
    },
    {
      // Directly under Menus and above the developer corner, because it is the same kind of
      // work as the two rows above it — wording an owner writes once and reuses — and because
      // it is what a broadcast in Chatwoot's own Campaigns group can only choose FROM. It sits
      // in this group rather than beside Broadcasts for a fork reason as much as a product one:
      // adding a row there means editing `campaigns.routes.js`, an upstream file this repo
      // already carries one deviation in, and this whole feature is additive.
      //
      // NOT `Templates`. Chatwoot spends that exact word on its own read-only Templates page in
      // the same rail (`settings.json` SIDEBAR), and two rows reading the same word is the most
      // common way an owner opens the wrong screen — the rule `specs/vocabulary.spec.js`
      // enforces. `Approved messages` is also what the rest of this product already calls them.
      // ...and FORMS is a tab on THIS row, for the reason the Forms row used to give for
      // sitting next to it: "both rows are about something Meta holds on the business's own
      // WhatsApp account and reviews before it works, and both are scoped to ONE number."
      // That is a description of one screen with two lists, which is what it now is —
      // `[ Approved messages | Forms ]`, the tab in the query (`?tab=forms`).
      //
      // `waslo_forms` joins this `activeOn` for the same reason the menu names joined Tasks:
      // the name stays registered (as a redirect), and a row that is the only way to a screen
      // has to light up when somebody is standing on it.
      name: 'WasloTemplates',
      label: wasloT(t, 'WASLO.NAV.TEMPLATES', locale),
      icon: 'i-lucide-badge-check',
      to: accountScopedRoute('waslo_templates'),
      activeOn: [
        'waslo_templates',
        'waslo_template_new',
        'waslo_template_edit',
        'waslo_forms',
      ],
    },
    {
      // The second sub-group, and a SIBLING of Settings rather than a child of it: this
      // is not a preference about the assistant, it is a separate way into the same
      // WhatsApp number — the owner's own systems sending through it.
      //
      // WHY THIS ONE CAN BE `collapsible` NOW, AND COULD NOT BEFORE. A collapsed group
      // hides its children, and this group used to hold `waslo_outbound_numbers` — the
      // ONLY opt-out list in the product, and the one row here that is the owner's
      // concern rather than her developer's. Making the caption collapsible while that
      // row was inside it traded a scannability win for the risk of burying a
      // do-not-contact list. Moving that row out to Campaigns (see
      // `campaigns/pages/broadcasts/nav.js`) is what discharges the precondition; the two
      // changes have to ship together or not at all.
      //
      // NOTE WHAT IS ABSENT: a `to`. `SidebarSubGroup.vue` never reads one — only the
      // TOP-LEVEL group header can also be a destination, and this one no longer is
      // either. The parent route's own redirect (`waslo_outbound` →
      // `waslo_outbound_setup`, the first child below) is the equivalent guarantee.
      // `specs/outbound-wiring.spec.js` pins the two together.
      name: 'WasloOutbound',
      label: wasloT(t, 'WASLO.NAV.OUTBOUND', locale),
      icon: 'i-lucide-code-xml',
      collapsible: true,
      showTreeLine: true,
      children: [
        {
          name: 'WasloOutboundSetup',
          label: wasloT(t, 'WASLO.NAV.OUTBOUND_SETUP', locale),
          icon: 'i-lucide-key-round',
          to: accountScopedRoute('waslo_outbound_setup'),
          // `waslo_outbound` is the parent record and redirects here, so a landing on it
          // must light this row up rather than none of them.
          activeOn: ['waslo_outbound', 'waslo_outbound_setup'],
        },
        {
          name: 'WasloOutboundLog',
          label: wasloT(t, 'WASLO.NAV.OUTBOUND_LOG', locale),
          icon: 'i-lucide-send',
          to: accountScopedRoute('waslo_outbound_log'),
          // The detail page is a child of the log in the owner's head and nowhere in the
          // route tree, so the row has to claim it explicitly — the same shape Tasks and
          // its editors use.
          activeOn: ['waslo_outbound_log', 'waslo_outbound_message'],
        },
        {
          // WAS `Settings -> Connections`, and it is here rather than gone because it is
          // the one thing in the old Settings group that genuinely belongs to a developer:
          // the owner's own systems, which the assistant calls mid-conversation. The route
          // name and both editor routes are untouched.
          name: 'WasloConnections',
          label: wasloT(t, 'WASLO.NAV.CONNECTIONS', locale),
          icon: 'i-lucide-plug',
          to: accountScopedRoute('waslo_settings_connections'),
          activeOn: [
            'waslo_settings_connections',
            'waslo_connection_new',
            'waslo_connection_edit',
          ],
        },
      ],
    },
  ];

  return {
    name: 'WasloAI',
    label: wasloT(t, 'WASLO.NAV.SECTION', locale),
    icon: WASLO_SIDEBAR_ICON,
    children,
  };
}

/**
 * The entry, or nothing at all — the shape `Sidebar.vue` spreads.
 *
 * A WORKSPACE WITH NO ASSISTANT MUST NOT SEE THIS GROUP. Every screen under it can only
 * answer `not_provisioned` there, so the group is fifteen doors onto the same failure. The
 * gate is an ARRAY rather than a `v-if` in `Sidebar.vue` for one reason: it keeps the
 * decision here, next to the entry it removes, where a spec can read it without mounting
 * Chatwoot's whole sidebar — `specs/provisioning-gate.spec.js`.
 *
 * The caller passes the answer rather than reading it, because `Sidebar.vue` already holds
 * it for the Campaigns rows too and one read cannot disagree with itself. See
 * `composables/useWasloProvisioned.js` for why the default is "hide".
 *
 * @param {(key: string) => string} t
 * @param {(name: string, params?: object, query?: object) => object} accountScopedRoute
 * @param {string} [locale]
 * @param {boolean} [provisioned] Does this workspace have an assistant? Absent → no.
 * @returns {object[]} Exactly one entry, or none.
 */
export function wasloSidebarItems(t, accountScopedRoute, locale, provisioned) {
  if (provisioned !== true) return [];
  return [wasloSidebarItem(t, accountScopedRoute, locale)];
}

export default wasloSidebarItem;
